/**
 * Drafts (Slack "Drafts & sent", docs/design/slack-gap.md): what I typed in a conversation's composer and didn't send,
 * kept per person on the server so it follows me to my other devices. One draft per conversation (a channel's
 * composer, or one thread's). The composer keeps a local copy too, so drafts work offline; the newer of the two wins
 * (`updatedAt`, set by the client that typed it). Text only — attached files stay on the device that picked them.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

export const Draft = z.object({
  id: Id,
  channelId: Id,
  /** A thread composer's draft. */
  threadRootId: Id.optional(),
  text: z.string().max(40_000),
  /** When it was last typed (the typing client's clock). */
  updatedAt: Timestamp,
});
export type Draft = z.infer<typeof Draft>;

/** GET /v1/w/:ws/drafts?channelId= — my drafts (one conversation and its threads, or all), most recent first. */
export const ListDraftsQuery = z.object({ channelId: Id.optional() });
export type ListDraftsQuery = z.infer<typeof ListDraftsQuery>;
export const ListDraftsResponse = z.object({ drafts: z.array(Draft) });
export type ListDraftsResponse = z.infer<typeof ListDraftsResponse>;

/**
 * PUT /v1/w/:ws/drafts — saves a conversation's draft (empty text clears it). Ignored when the server already has a
 * newer one (`updatedAt`); the response is what the server keeps (absent = no draft there).
 */
export const SaveDraftRequest = z.object({
  channelId: Id,
  threadRootId: Id.optional(),
  text: z.string().max(40_000),
  updatedAt: Timestamp,
});
export type SaveDraftRequest = z.infer<typeof SaveDraftRequest>;
export const SaveDraftResponse = z.object({ draft: Draft.optional() });
export type SaveDraftResponse = z.infer<typeof SaveDraftResponse>;
